/**
 * AuthService - JWT-based multi-provider authentication service
 *
 * Replaces MSAL-based authClient with a generic JWT auth service supporting:
 * - Email/password login and registration
 * - Microsoft OAuth (via backend redirect)
 * - Google OAuth (via backend redirect)
 * - Password reset flow
 * - Email verification flow
 */

import { logger } from '../utils/logger';
import type {
  User,
  AuthResult,
  LoginCredentials,
  RegisterCredentials,
  ResetPasswordData,
  VerifyEmailData,
  OAuthProvider
} from '../shared/auth-types';

const API_BASE_URL = import.meta.env.VITE_CORPUS_API_BASE_URL || '';
const TOKEN_KEY = 'corpus_auth_token';
const REQUEST_TIMEOUT = 30000; // 30 seconds

export type AuthChangeCallback = (authenticated: boolean, user?: User) => void;

/**
 * Fetch with timeout to prevent infinite loading
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout = REQUEST_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Decode JWT payload without verification (client-side only)
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed for proper base64 decoding
    while (payload.length % 4 !== 0) {
      payload += '=';
    }
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if JWT token is expired
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  // Add 60 second buffer for clock skew
  return payload.exp * 1000 < Date.now() + 60000;
}

class AuthService {
  private listeners: Set<AuthChangeCallback> = new Set();
  private cachedUser: User | null = null;
  private initialized = false;
  private initPromise: Promise<void>;

  constructor() {
    this.initPromise = this.initialize();
  }

  private async initialize(): Promise<void> {
    logger.log('[AuthService] Initializing...');
    const token = this.getToken();

    if (token && !isTokenExpired(token)) {
      // Fetch user profile to validate token and get user info
      try {
        const user = await this.fetchUserProfile();
        if (user) {
          this.cachedUser = user;
          logger.log('[AuthService] Initialized with valid token for:', user.email);
        } else {
          // Token invalid, clear it
          this.clearToken();
          logger.log('[AuthService] Token invalid, cleared');
        }
      } catch (err) {
        logger.error('[AuthService] Failed to fetch user profile:', err);
        this.clearToken();
      }
    } else if (token) {
      logger.log('[AuthService] Token expired, clearing');
      this.clearToken();
    }

    this.initialized = true;
    logger.log('[AuthService] Initialization complete');
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initPromise;
    }
  }

  /**
   * Get stored JWT token
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Store JWT token
   */
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Clear stored token
   */
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.cachedUser = null;
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    await this.ensureInitialized();
    const token = this.getToken();
    return !!token && !isTokenExpired(token);
  }

  /**
   * Get current user
   */
  async getUser(): Promise<User | null> {
    await this.ensureInitialized();
    return this.cachedUser;
  }

  /**
   * Fetch user profile from /me endpoint
   */
  private async fetchUserProfile(): Promise<User | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return {
          id: data.id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          company: data.company,
          company_id: data.company_id,
          is_admin: data.is_admin ?? false,
          is_active: data.is_active ?? data.isactive ?? true,
          is_verified: data.is_verified ?? true
        };
      }

      if (response.status === 401) {
        this.clearToken();
        this.notifyListeners(false);
      }

      return null;
    } catch (err) {
      logger.error('[AuthService] fetchUserProfile error:', err);
      return null;
    }
  }

  /**
   * Obfuscate email for logging (privacy)
   */
  private obfuscateEmail(email: string): string {
    if (!email || !email.includes('@')) return '***';
    const [local, domain] = email.split('@');
    return `${local.substring(0, 2)}***@${domain}`;
  }

  /**
   * Register with email/password
   */
  async register(credentials: RegisterCredentials): Promise<AuthResult> {
    const url = `${API_BASE_URL}/api/auth/register`;
    logger.log('[AuthService] register: Starting registration for', this.obfuscateEmail(credentials.email));
    logger.log('[AuthService] register: POST', url);

    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      logger.log('[AuthService] register: Response status', response.status);

      const data = await response.json();
      logger.log('[AuthService] register: Response data', data);

      if (response.ok) {
        logger.log('[AuthService] register: Success');
        return {
          success: true,
          requiresVerification: true
        };
      }

      const errorMsg = data.detail || data.message || 'Registration failed';
      logger.error('[AuthService] register: Failed -', errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    } catch (err: any) {
      if (err.name === 'AbortError') {
        logger.error('[AuthService] register: Request timeout after 30s');
        return {
          success: false,
          error: 'La requête a expiré. Vérifiez votre connexion.'
        };
      }
      logger.error('[AuthService] register error:', err);
      return {
        success: false,
        error: err.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Login with email/password
   * Note: fastapi-users uses OAuth2PasswordRequestForm which requires
   * application/x-www-form-urlencoded with 'username' field (not 'email')
   */
  async loginWithEmail(credentials: LoginCredentials): Promise<AuthResult> {
    const url = `${API_BASE_URL}/api/auth/login`;
    logger.log('[AuthService] loginWithEmail: Starting login for', this.obfuscateEmail(credentials.email));
    logger.log('[AuthService] loginWithEmail: POST', url);

    try {
      // fastapi-users OAuth2PasswordRequestForm requires form data with 'username' field
      const formData = new URLSearchParams({
        username: credentials.email,  // OAuth2 spec uses 'username', not 'email'
        password: credentials.password
      });

      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData
      });

      logger.log('[AuthService] loginWithEmail: Response status', response.status);
      const data = await response.json();
      logger.log('[AuthService] loginWithEmail: Response data', data);

      if (response.ok && data.access_token) {
        this.setToken(data.access_token);
        const user = await this.fetchUserProfile();
        this.cachedUser = user;
        this.notifyListeners(true, user || undefined);

        logger.log('[AuthService] loginWithEmail: Success for', this.obfuscateEmail(credentials.email));
        return {
          success: true,
          token: data.access_token,
          user: user || undefined
        };
      }

      // Handle unverified email - check error_code first, fall back to string matching
      if (response.status === 403 && (data.error_code === 'EMAIL_NOT_VERIFIED' || data.requiresVerification || data.detail?.includes('verify'))) {
        logger.log('[AuthService] loginWithEmail: Email not verified');
        return {
          success: false,
          error: data.detail,
          requiresVerification: true
        };
      }

      const errorMsg = data.detail || data.message || 'Login failed';
      logger.error('[AuthService] loginWithEmail: Failed -', errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    } catch (err: any) {
      if (err.name === 'AbortError') {
        logger.error('[AuthService] loginWithEmail: Request timeout after 30s');
        return {
          success: false,
          error: 'La requête a expiré. Vérifiez votre connexion.'
        };
      }
      logger.error('[AuthService] loginWithEmail error:', err);
      return {
        success: false,
        error: err.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Initiate OAuth login - navigates to backend OAuth endpoint
   * Backend can either:
   * 1. Return JSON with authorization_url (SPA mode)
   * 2. Return 302 redirect (standard mode)
   */
  async loginWithOAuth(provider: OAuthProvider): Promise<{ success: boolean; error?: string }> {
    // Add prompt=select_account to force account picker on Microsoft OAuth
    const baseUrl = `${API_BASE_URL}/api/auth/${provider}/authorize`;
    const url = provider === 'microsoft' ? `${baseUrl}?prompt=select_account` : baseUrl;
    logger.log(`[AuthService] loginWithOAuth: Starting ${provider} OAuth`);

    try {
      // First, try to fetch with redirect: 'manual' to check response type
      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        redirect: 'manual' // Don't follow redirects automatically
      });

      logger.log(`[AuthService] loginWithOAuth: Response status`, response.status, response.type);

      // If backend returns a redirect (302), navigate directly to the endpoint
      // response.type will be 'opaqueredirect' when redirect is manual
      if (response.type === 'opaqueredirect' || response.status === 0 || response.status === 302) {
        logger.log(`[AuthService] loginWithOAuth: Backend does 302 redirect, navigating to`, url);
        window.location.href = url;
        return { success: true };
      }

      // Parse response body once to avoid "body stream already read" error
      let data: Record<string, unknown> = {};
      try {
        data = await response.json();
      } catch {
        // Body might be empty or not JSON
      }

      logger.log(`[AuthService] loginWithOAuth: Response data`, data);

      // If backend returns JSON with authorization_url
      if (response.ok && data.authorization_url) {
        // Ensure prompt=select_account is in the URL for Microsoft
        let authUrl = data.authorization_url as string;
        if (provider === 'microsoft' && !authUrl.includes('prompt=')) {
          const separator = authUrl.includes('?') ? '&' : '?';
          authUrl = `${authUrl}${separator}prompt=select_account`;
        }
        logger.log(`[AuthService] loginWithOAuth: Redirecting to`, authUrl);
        window.location.href = authUrl;
        return { success: true };
      }

      // Error case - reuse already-parsed data
      const errorMsg = (data.detail || (data.error as Record<string, unknown>)?.message || `${provider} OAuth non disponible`) as string;
      logger.error(`[AuthService] loginWithOAuth: Failed -`, errorMsg);
      return { success: false, error: errorMsg };

    } catch (err: any) {
      if (err.name === 'AbortError') {
        logger.error(`[AuthService] loginWithOAuth: Request timeout after 30s`);
        return { success: false, error: 'La requête a expiré. Vérifiez votre connexion.' };
      }
      logger.error(`[AuthService] loginWithOAuth error:`, err);
      return { success: false, error: err.message || 'Erreur réseau' };
    }
  }

  /**
   * Handle OAuth callback - store token from URL
   */
  async handleOAuthCallback(token: string): Promise<AuthResult> {
    if (!token) {
      return { success: false, error: 'No token provided' };
    }

    this.setToken(token);
    const user = await this.fetchUserProfile();

    if (user) {
      this.cachedUser = user;
      this.notifyListeners(true, user);
      return { success: true, token, user };
    }

    this.clearToken();
    return { success: false, error: 'Failed to fetch user profile' };
  }

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<AuthResult> {
    const url = `${API_BASE_URL}/api/auth/forgot-password`;
    logger.log('[AuthService] forgotPassword: Requesting reset for', this.obfuscateEmail(email));
    logger.log('[AuthService] forgotPassword: POST', url);

    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      logger.log('[AuthService] forgotPassword: Response status', response.status);
      const data = await response.json();
      logger.log('[AuthService] forgotPassword: Response data', data);

      if (response.ok) {
        logger.log('[AuthService] forgotPassword: Success');
        return { success: true };
      }

      const errorMsg = data.detail || data.message || 'Failed to send reset email';
      logger.error('[AuthService] forgotPassword: Failed -', errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    } catch (err: any) {
      if (err.name === 'AbortError') {
        logger.error('[AuthService] forgotPassword: Request timeout after 30s');
        return {
          success: false,
          error: 'La requête a expiré. Vérifiez votre connexion.'
        };
      }
      logger.error('[AuthService] forgotPassword error:', err);
      return {
        success: false,
        error: err.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordData): Promise<AuthResult> {
    const url = `${API_BASE_URL}/api/auth/reset-password`;
    logger.log('[AuthService] resetPassword: Resetting password with token');
    logger.log('[AuthService] resetPassword: POST', url);

    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      logger.log('[AuthService] resetPassword: Response status', response.status);
      const responseData = await response.json();
      logger.log('[AuthService] resetPassword: Response data', responseData);

      if (response.ok) {
        logger.log('[AuthService] resetPassword: Success');
        return { success: true };
      }

      const errorMsg = responseData.detail || responseData.message || 'Failed to reset password';
      logger.error('[AuthService] resetPassword: Failed -', errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    } catch (err: any) {
      if (err.name === 'AbortError') {
        logger.error('[AuthService] resetPassword: Request timeout after 30s');
        return {
          success: false,
          error: 'La requête a expiré. Vérifiez votre connexion.'
        };
      }
      logger.error('[AuthService] resetPassword error:', err);
      return {
        success: false,
        error: err.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Verify email with token
   */
  async verifyEmail(data: VerifyEmailData): Promise<AuthResult> {
    const url = `${API_BASE_URL}/api/auth/verify`;
    logger.log('[AuthService] verifyEmail: Verifying email with token');
    logger.log('[AuthService] verifyEmail: POST', url);

    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      logger.log('[AuthService] verifyEmail: Response status', response.status);
      const responseData = await response.json();
      logger.log('[AuthService] verifyEmail: Response data', responseData);

      if (response.ok) {
        logger.log('[AuthService] verifyEmail: Success');
        return { success: true };
      }

      const errorMsg = responseData.detail || responseData.message || 'Failed to verify email';
      logger.error('[AuthService] verifyEmail: Failed -', errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    } catch (err: any) {
      if (err.name === 'AbortError') {
        logger.error('[AuthService] verifyEmail: Request timeout after 30s');
        return {
          success: false,
          error: 'La requête a expiré. Vérifiez votre connexion.'
        };
      }
      logger.error('[AuthService] verifyEmail error:', err);
      return {
        success: false,
        error: err.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Request new verification email
   */
  async requestVerificationEmail(email: string): Promise<AuthResult> {
    const url = `${API_BASE_URL}/api/auth/request-verify-token`;
    logger.log('[AuthService] requestVerificationEmail: Requesting for', this.obfuscateEmail(email));
    logger.log('[AuthService] requestVerificationEmail: POST', url);

    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      logger.log('[AuthService] requestVerificationEmail: Response status', response.status);
      const data = await response.json();
      logger.log('[AuthService] requestVerificationEmail: Response data', data);

      if (response.ok) {
        logger.log('[AuthService] requestVerificationEmail: Success');
        return { success: true };
      }

      const errorMsg = data.detail || data.message || 'Failed to send verification email';
      logger.error('[AuthService] requestVerificationEmail: Failed -', errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    } catch (err: any) {
      if (err.name === 'AbortError') {
        logger.error('[AuthService] requestVerificationEmail: Request timeout after 30s');
        return {
          success: false,
          error: 'La requête a expiré. Vérifiez votre connexion.'
        };
      }
      logger.error('[AuthService] requestVerificationEmail error:', err);
      return {
        success: false,
        error: err.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Logout - clear token and notify listeners
   */
  async logout(): Promise<void> {
    this.clearToken();
    this.notifyListeners(false);
    logger.log('[AuthService] Logged out');
  }

  /**
   * Refresh user profile from backend
   */
  async refreshUser(): Promise<User | null> {
    const user = await this.fetchUserProfile();
    this.cachedUser = user;
    if (user) {
      this.notifyListeners(true, user);
    } else {
      this.notifyListeners(false);
    }
    return user;
  }

  /**
   * Subscribe to auth state changes
   */
  onAuthChanged(callback: AuthChangeCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(authenticated: boolean, user?: User): void {
    this.listeners.forEach(callback => callback(authenticated, user));
  }
}

// Default singleton instance
export const authService = new AuthService();
